import { useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Upload, FileSpreadsheet, CheckCircle2 } from "lucide-react";
import { saveBlob } from "@/lib/labels";
import { csvToProspectRows, TEMPLATE_HEADERS } from "@/lib/csvImport";

const ACTION_STYLE = { add: "text-emerald-600", update: "text-sky-600", unchanged: "text-muted-foreground", skip: "text-amber-600" };
const ACTION_LABEL = { add: "Add", update: "Update", unchanged: "No change", skip: "Skip" };

const Summary = ({ counts, done }) => (
  <div className="grid grid-cols-4 gap-2 text-center" data-testid="import-summary">
    {["add", "update", "unchanged", "skip"].map((a) => (
      <div key={a} className="rounded-lg border border-border p-2" data-testid={`import-count-${a}`}>
        <div className={`text-lg font-bold tabular-nums ${ACTION_STYLE[a]}`}>{counts[a] || 0}</div>
        <div className="text-xs text-muted-foreground">{done ? { add: "Added", update: "Updated", unchanged: "No change", skip: "Skipped" }[a] : ACTION_LABEL[a]}</div>
      </div>
    ))}
  </div>
);

const RowList = ({ rows }) => (
  <div className="max-h-64 overflow-y-auto rounded-lg border border-border divide-y divide-border text-xs" data-testid="import-rows">
    {rows.map((r) => (
      <div key={r.row} className="flex gap-2 px-3 py-1.5" data-testid={`import-row-${r.row}`}>
        <span className="w-10 text-muted-foreground tabular-nums">#{r.row}</span>
        <span className={`w-16 font-medium ${ACTION_STYLE[r.action]}`}>{ACTION_LABEL[r.action]}</span>
        <span className="flex-1 truncate">{r.name || "—"}</span>
        <span className="flex-1 truncate text-muted-foreground" title={r.message}>{r.message}</span>
      </div>
    ))}
  </div>
);

export const ImportProspectsDialog = ({ open, onOpenChange, onDone }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setFile(null); setPreview(null); setResult(null); };
  const close = (o) => { if (!o) reset(); onOpenChange(o); };

  const send = async (dry_run) => {
    const { data } = await api.post("/prospects/import", { rows: file.rows, dry_run });
    return data;
  };

  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const parsed = csvToProspectRows(await f.text());
    if (parsed.missingName) { toast.error("The file needs a \"Business\" column"); return; }
    if (!parsed.rows.length) { toast.error("No prospect rows found in the file"); return; }
    const next = { name: f.name, ...parsed };
    setFile(next); setResult(null); setBusy(true);
    try {
      const { data } = await api.post("/prospects/import", { rows: next.rows, dry_run: true });
      setPreview(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); setFile(null); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const data = await send(false);
      setResult(data);
      toast.success(`Imported: ${data.counts.add} added, ${data.counts.update} updated`);
      onDone?.();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const template = () => saveBlob(new Blob([TEMPLATE_HEADERS.join(",") + "\r\n"], { type: "text/csv;charset=utf-8" }), "Prospects_Import_Template.csv");
  const shown = result || preview;
  const changes = (preview?.counts.add || 0) + (preview?.counts.update || 0);

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-2xl" data-testid="import-prospects-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><Upload className="w-5 h-5 text-primary" />Import Prospects from CSV</DialogTitle>
          <DialogDescription>
            Use the same columns as Export CSV: Business, Street Address, City, State, Zip Code, plus any of Type, Phone, Website, Email, Contact, Zone #, Status, Notes, Last Contacted. Rows that match an existing prospect by business name (and street, if several share a name) are updated; blank cells never erase existing details. Everything else is added as a new prospect.
          </DialogDescription>
        </DialogHeader>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={pick} data-testid="import-file-input" />
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="import-choose-file-btn"><FileSpreadsheet className="w-4 h-4 mr-1.5" />{file ? "Choose a different file" : "Choose CSV file"}</Button>
          <Button variant="ghost" size="sm" onClick={template} data-testid="import-template-btn">Download blank template</Button>
          {file && <span className="text-xs text-muted-foreground" data-testid="import-file-name">{file.name} · {file.rows.length} rows</span>}
        </div>
        {busy && !shown && <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="import-checking"><Loader2 className="w-4 h-4 animate-spin" />Checking your file…</div>}
        {shown && (
          <div className="space-y-3">
            {result && <div className="flex items-center gap-2 text-sm" data-testid="import-done"><CheckCircle2 className="w-4 h-4 text-emerald-500" />Import complete.{result.counts.mapping > 0 && ` Placing ${result.counts.mapping} on the map in the background (about one per second).`} If new rows don't appear, clear the Type and other filters.</div>}
            {!result && <div className="text-sm text-muted-foreground" data-testid="import-preview-note">Preview — nothing has been saved yet.</div>}
            <Summary counts={shown.counts} done={!!result} />
            <RowList rows={shown.rows} />
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => close(false)} data-testid="import-close-btn">{result ? "Done" : "Cancel"}</Button>
          {preview && !result && (
            <Button onClick={commit} disabled={busy || !changes} data-testid="import-confirm-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}Import {changes} prospect{changes === 1 ? "" : "s"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
